import { Avatar } from "@chakra-ui/avatar";
import { Tooltip } from "@chakra-ui/tooltip";
import { Fragment, useEffect, useState } from "react";
import ScrollableFeed from "react-scrollable-feed";
import {
  isLastMessage,
  isSameSender,
  isSameSenderMargin,
  isSameUser,
} from "../config/ChatLogics";
import { ChatState } from "../Context/ChatProvider";

const formatTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("ru-RU", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
};

// ссылки кликабельны, @упоминания подсвечены
const TOKEN_RE = /((?:https?:\/\/|www\.)\S+|@[\wа-яё_-]+)/giu;
const IS_URL_RE = /^(?:https?:\/\/|www\.)\S+$/i;
const IS_MENTION_RE = /^@[\wа-яё_-]+$/iu;
const TRAILING_PUNCT_RE = /[),.!?;:'"»\]]+$/;

const renderContent = (text, isMine) => {
  if (!text) return null;
  const parts = text.split(TOKEN_RE);
  return parts.map((part, i) => {
    if (IS_URL_RE.test(part)) {
      let url = part;
      let trail = "";
      const m = url.match(TRAILING_PUNCT_RE);
      if (m) {
        trail = m[0];
        url = url.slice(0, -trail.length);
      }
      const href = url.startsWith("www.") ? `https://${url}` : url;
      return (
        <Fragment key={i}>
          <a
            href={href}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{
              color: isMine ? "#ECCFA7" : "#8D414B",
              textDecoration: "underline",
              wordBreak: "break-all",
            }}
          >
            {url}
          </a>
          {trail}
        </Fragment>
      );
    }
    if (IS_MENTION_RE.test(part)) {
      return (
        <span
          key={i}
          style={{
            color: isMine ? "#ECCFA7" : "#8D414B",
            fontWeight: 600,
          }}
        >
          {part}
        </span>
      );
    }
    return part;
  });
};

const FileGlyph = ({ color }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M13 3H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V9l-6-6z"
      stroke={color}
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path d="M13 3v6h6" stroke={color} strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);

// Просмотрщик изображений поверх всего
const Lightbox = ({ att, onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!att) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(20, 35, 23, 0.93)",
        zIndex: 2000,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        cursor: "zoom-out",
      }}
    >
      <img
        src={att.url}
        alt={att.name || ""}
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: "92vw",
          maxHeight: "84vh",
          borderRadius: "12px",
          boxShadow: "0 30px 90px rgba(0,0,0,0.5)",
          cursor: "default",
        }}
      />
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          marginTop: "14px",
          display: "flex",
          alignItems: "center",
          gap: "16px",
          color: "#F2DFB6",
          fontSize: "13px",
          cursor: "default",
        }}
      >
        <span style={{ opacity: 0.8 }}>{att.name}</span>
        <a
          href={att.url}
          download={att.name}
          style={{
            color: "#ECCFA7",
            border: "1px solid rgba(236,207,167,.5)",
            borderRadius: "10px",
            padding: "5px 14px",
            textDecoration: "none",
          }}
        >
          Скачать
        </a>
      </div>
      <button
        onClick={onClose}
        aria-label="Закрыть"
        style={{
          position: "fixed",
          top: "18px",
          right: "22px",
          background: "rgba(242,223,182,0.12)",
          border: "none",
          color: "#F2DFB6",
          fontSize: "20px",
          width: "40px",
          height: "40px",
          borderRadius: "50%",
          cursor: "pointer",
        }}
      >
        ✕
      </button>
    </div>
  );
};

const ScrollableChat = ({ messages, canDelete, onDelete, onEdit, onReply }) => {
  const { user } = ChatState();
  const [lightbox, setLightbox] = useState(null);

  return (
    <>
      <Lightbox att={lightbox} onClose={() => setLightbox(null)} />
      <ScrollableFeed>
        {messages &&
          messages.map((m, i) => {
            const isMine = m.sender._id === user._id;
            const att = m.attachment && m.attachment.url ? m.attachment : null;
            const isVideo = att && att.mime && att.mime.startsWith("video/");
            const isAudio = att && att.mime && att.mime.startsWith("audio/");
            const isImage = att && att.kind === "image";
            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  maxWidth: "100%",
                  opacity: m.pending ? 0.55 : 1,
                  transition: "opacity 0.2s ease",
                }}
                key={m._id}
                className="bubble-wrap"
              >
                {(isSameSender(messages, m, i, user._id) ||
                  isLastMessage(messages, i, user._id)) && (
                  <Tooltip
                    label={m.sender.name}
                    placement="bottom-start"
                    hasArrow
                  >
                    <Avatar
                      mt="7px"
                      mr={2}
                      size="sm"
                      cursor="pointer"
                      name={m.sender.name}
                      src={m.sender.pic}
                    />
                  </Tooltip>
                )}
                <span
                  className="bubble"
                  style={{
                    backgroundColor: isMine ? "#243D27" : "#FDF7E9",
                    color: isMine ? "#F2DFB6" : "#243D27",
                    border: isMine
                      ? "1px solid #243D27"
                      : "1px solid #EAD9AC",
                    boxShadow: isMine
                      ? "0 4px 14px -6px rgba(20, 35, 23, 0.5)"
                      : "0 3px 10px -6px rgba(36, 61, 39, 0.25)",
                    marginLeft: isSameSenderMargin(messages, m, i, user._id),
                    marginTop: isSameUser(messages, m, i, user._id) ? 4 : 12,
                    borderRadius: isMine
                      ? "18px 18px 6px 18px"
                      : "18px 18px 18px 6px",
                    padding: isImage || isVideo ? "6px" : "8px 14px 6px 16px",
                    maxWidth: "min(75%, 320px)",
                    minWidth: 0,
                    boxSizing: "border-box",
                    display: "inline-flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  {m.replyTo && (
                    <span
                      style={{
                        borderLeft: `3px solid ${isMine ? "#ECCFA7" : "#8D414B"}`,
                        background: isMine
                          ? "rgba(242, 223, 182, 0.12)"
                          : "rgba(141, 65, 75, 0.07)",
                        borderRadius: "8px",
                        padding: "4px 10px",
                        margin: "0 0 6px 0",
                        fontSize: "12px",
                        display: "block",
                        minWidth: 0,
                        boxSizing: "border-box",
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          color: isMine ? "#ECCFA7" : "#8D414B",
                          display: "block",
                        }}
                      >
                        {m.replyTo.sender?.name || "Сообщение"}
                      </span>
                      <span
                        style={{
                          opacity: 0.75,
                          display: "block",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "100%",
                        }}
                      >
                        {m.replyTo.content ||
                          (m.replyTo.attachment?.url ? "вложение" : "…")}
                      </span>
                    </span>
                  )}
                  {isImage && (
                    <img
                      src={att.url}
                      alt={att.name || "изображение"}
                      onClick={() => setLightbox(att)}
                      style={{
                        maxWidth: "100%",
                        width: "min(260px, 62vw)",
                        maxHeight: "260px",
                        borderRadius: "13px",
                        display: "block",
                        objectFit: "cover",
                        cursor: "zoom-in",
                      }}
                    />
                  )}
                  {isVideo && (
                    <video
                      src={att.url}
                      controls
                      preload="metadata"
                      style={{
                        maxWidth: "100%",
                        width: "min(280px, 68vw)",
                        borderRadius: "13px",
                        display: "block",
                      }}
                    />
                  )}
                  {isAudio && (
                    <audio
                      src={att.url}
                      controls
                      preload="metadata"
                      style={{ maxWidth: "100%", width: "min(260px, 62vw)" }}
                    />
                  )}
                  {att && !isImage && !isVideo && !isAudio && (
                    <a
                      href={att.url}
                      download={att.name}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        textDecoration: "none",
                        color: "inherit",
                        minWidth: 0,
                      }}
                    >
                      <FileGlyph color={isMine ? "#ECCFA7" : "#8D414B"} />
                      <span style={{ minWidth: 0 }}>
                        <span
                          style={{
                            display: "block",
                            fontWeight: 500,
                            fontSize: "14px",
                            wordBreak: "break-all",
                          }}
                        >
                          {att.name || "файл"}
                        </span>
                        <span style={{ fontSize: "11px", opacity: 0.65 }}>
                          {formatSize(att.size)}
                        </span>
                      </span>
                    </a>
                  )}
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "flex-end",
                      gap: "10px",
                      minWidth: 0,
                      padding:
                        isImage || isVideo ? "2px 8px 0 8px" : 0,
                    }}
                  >
                    {m.content && (
                      <span
                        style={{
                          lineHeight: 1.45,
                          minWidth: 0,
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                          whiteSpace: "pre-wrap", // сохраняем переносы строк
                        }}
                      >
                        {renderContent(m.content, isMine)}
                      </span>
                    )}
                    <span
                      style={{
                        fontSize: "10.5px",
                        opacity: 0.62,
                        whiteSpace: "nowrap",
                        transform: "translateY(2px)",
                        marginLeft: m.content ? 0 : "auto",
                      }}
                    >
                      {m.edited ? "изм. · " : ""}
                      {m.pending ? "⋯" : formatTime(m.createdAt)}
                    </span>
                  </span>
                </span>
                {!m.pending && onReply && (
                  <button
                    className="msg-del"
                    title="Ответить"
                    onClick={() => onReply(m)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#243D27",
                      padding: "0 3px 0 6px",
                      lineHeight: 1,
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M10 9V5.8c0-.66-.78-1-1.26-.56L2.6 10.9a.75.75 0 000 1.12l6.14 5.66c.48.44 1.26.1 1.26-.56V13.9c4.2 0 7.3 1.34 9.5 4.28.3.4.94.17.86-.32C19.6 12.6 16.1 9.6 10 9z" />
                    </svg>
                  </button>
                )}
                {!m.pending && isMine && m.content && onEdit && (
                  <button
                    className="msg-del"
                    title="Редактировать"
                    onClick={() => onEdit(m)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#243D27",
                      padding: "0 3px 0 6px",
                      lineHeight: 1,
                    }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 000-1.41l-2.34-2.34a1 1 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                    </svg>
                  </button>
                )}
                {!m.pending && canDelete && canDelete(m) && (
                  <button
                    className="msg-del"
                    title="Удалить сообщение"
                    onClick={() => onDelete(m)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#8D414B",
                      fontSize: "14px",
                      padding: "0 6px 0 3px",
                      lineHeight: 1,
                    }}
                  >
                    ✕
                  </button>
                )}
              </div>
            );
          })}
      </ScrollableFeed>
    </>
  );
};

export default ScrollableChat;
